import React, { useState } from 'react';
import { List, useTable, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select, Image, Button, Typography } from 'antd';
import { SearchOutlined, LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { AdminFoodLogListItemDto, FoodLogSource, ScanFeedback, CatalogSource } from '@wellverse/shared-ts';
import { resolveCmsMediaUrl } from '../../utils/media-url';
import { GpsLocation } from '../../components/GpsLocation';

const { Text } = Typography;

const sourceColors: Record<FoodLogSource, string> = {
  [FoodLogSource.FOOD_SCAN]: '#3ecf8e',
  [FoodLogSource.FOOD_LABEL]: '#3ecf8e',
  [FoodLogSource.MANUAL]: '#898989',
  [FoodLogSource.BARCODE]: '#f5a623',
  [FoodLogSource.DESCRIBE_TEXT]: '#1890ff',
};

const feedbackColors: Record<ScanFeedback, string> = {
  [ScanFeedback.POSITIVE]: '#3ecf8e',
  [ScanFeedback.NEGATIVE]: '#ff6b6b',
};

const catalogSourceColors: Record<string, string> = {
  [CatalogSource.AI_GENERATED]: '#eb2f96',
  [CatalogSource.WELLCOME]: '#faad14',
  [CatalogSource.MANUAL]: '#1890ff',
  [CatalogSource.USDA]: '#52c41a',
  [CatalogSource.CFS]: '#13c2c2',
};

export const FoodLogList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [scanFeedback, setScanFeedback] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<AdminFoodLogListItemDto>({
    resource: 'food-logs',
    filters: {
      permanent: [
        ...(search ? [{ field: 'search', operator: 'eq' as const, value: search }] : []),
        ...(scanFeedback
          ? [{ field: 'scanFeedback', operator: 'eq' as const, value: scanFeedback }]
          : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="food-logs">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by food name..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={(v) => setSearch(v.trim())}
          allowClear
          onClear={() => {
            setSearchText('');
            setSearch('');
          }}
          style={{ width: 320 }}
        />
        <Select
          placeholder="Feedback"
          allowClear
          style={{ width: 180 }}
          value={scanFeedback}
          onChange={(v) => setScanFeedback(v)}
          options={[
            { label: 'Positive', value: 'POSITIVE' },
            { label: 'Negative', value: 'NEGATIVE' },
            { label: 'None', value: 'NONE' },
          ]}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          title="Image"
          dataIndex="mealImageUrl"
          width={72}
          render={(url: string | undefined) => {
            const src = resolveCmsMediaUrl(url);
            return src ? (
              <Image
                src={src}
                alt=""
                width={48}
                height={48}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect fill='%23f0f0f0' width='48' height='48'/%3E%3C/svg%3E"
                preview={{ mask: false }}
              />
            ) : (
              <Text type="secondary">—</Text>
            );
          }}
        />
        <Table.Column dataIndex="foodName" title="Food Name" />
        <Table.Column
          dataIndex="scanFeedback"
          title="Feedback"
          width={120}
          render={(fb: ScanFeedback | undefined) =>
            fb ? (
              <Tag
                style={{
                  borderColor: feedbackColors[fb] || '#898989',
                  color: feedbackColors[fb] || '#898989',
                  background: `${feedbackColors[fb] || '#898989'}15`,
                  borderRadius: 9999,
                  padding: '2px 12px',
                  fontWeight: 500,
                }}
              >
                {fb}
              </Tag>
            ) : (
              <Text type="secondary">—</Text>
            )
          }
        />
        <Table.Column
          title="Dish Catalog"
          width={220}
          render={(_: unknown, record: AdminFoodLogListItemDto) =>
            record.dishCatalogId ? (
              <Space direction="vertical" size={2}>
                <Link to={`/dish-catalog/show/${record.dishCatalogId}`}>
                  {record.dishCatalogName || record.dishCatalogId.slice(0, 8)}
                </Link>
                {record.dishCatalogSource && (
                  <Tag
                    style={{
                      borderColor: catalogSourceColors[record.dishCatalogSource] || '#898989',
                      color: catalogSourceColors[record.dishCatalogSource] || '#898989',
                      background: `${catalogSourceColors[record.dishCatalogSource] || '#898989'}15`,
                      borderRadius: 9999,
                      padding: '0 8px',
                      fontWeight: 500,
                      width: 'fit-content',
                    }}
                  >
                    {record.dishCatalogSource}
                  </Tag>
                )}
              </Space>
            ) : (
              <Text type="secondary">—</Text>
            )
          }
        />
        <Table.Column
          dataIndex="source"
          title="Source"
          filters={Object.values(FoodLogSource).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: AdminFoodLogListItemDto) => record.source === value}
          render={(source: FoodLogSource) => (
            <Tag
              style={{
                borderColor: sourceColors[source] || '#898989',
                color: sourceColors[source] || '#898989',
                background: `${sourceColors[source] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {source}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="amount"
          title="Amount"
          render={(_: number, record: AdminFoodLogListItemDto) =>
            `${record.amount} ${record.amountUnit}`
          }
        />
        <Table.Column dataIndex="peopleSharing" title="People" />
        <Table.Column
          title="Location"
          width={160}
          render={(_: unknown, record: AdminFoodLogListItemDto) => (
            <GpsLocation
              variant="cell"
              latitude={record.mealSetLatitude}
              longitude={record.mealSetLongitude}
            />
          )}
        />
        <Table.Column dataIndex="ingredientCount" title="Ingredients" />
        <Table.Column
          dataIndex="loggedAt"
          title="Logged At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          fixed="right"
          width={140}
          render={(_, record: AdminFoodLogListItemDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="food-logs" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="food-logs" />
              {record.visualEntityId && (
                <Link to={`/visual-entities/show/${record.visualEntityId}`}>
                  <Button
                    type="link"
                    size="small"
                    icon={<LinkOutlined />}
                    title="Visual Entity"
                  />
                </Link>
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
